/**
 * Light / dark / system color mode. The choice is a per-browser convenience
 * (localStorage); `.dark` on <html> switches the neutral tokens in global.css.
 */
export type ColorMode = 'light' | 'dark' | 'system';

const STORAGE_KEY = 'ui.colorMode';

export function readColorMode(): ColorMode {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return stored === 'light' || stored === 'dark' ? stored : 'system';
  } catch (error) {
    // Storage blocked (private mode, sandboxed frame): follow the system
    console.warn('[theme] could not read the saved color mode', error);
    return 'system';
  }
}

export function saveColorMode(mode: ColorMode): void {
  try {
    if (mode === 'system') window.localStorage.removeItem(STORAGE_KEY);
    else window.localStorage.setItem(STORAGE_KEY, mode);
  } catch (error) {
    console.warn('[theme] could not save the color mode', error);
  }
}

function systemPrefersDark(): boolean {
  return typeof window.matchMedia === 'function' && window.matchMedia('(prefers-color-scheme: dark)').matches;
}

export function resolveColorMode(mode: ColorMode): 'light' | 'dark' {
  return mode === 'system' ? (systemPrefersDark() ? 'dark' : 'light') : mode;
}

export function applyColorMode(mode: ColorMode): 'light' | 'dark' {
  const resolved = resolveColorMode(mode);
  document.documentElement.classList.toggle('dark', resolved === 'dark');
  return resolved;
}

/** Calls back when the system scheme changes (only matters in 'system' mode) */
export function onSystemColorModeChange(callback: () => void): () => void {
  if (typeof window.matchMedia !== 'function') return () => undefined;
  const query = window.matchMedia('(prefers-color-scheme: dark)');
  query.addEventListener('change', callback);
  return () => query.removeEventListener('change', callback);
}
